import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Settings, Eye, Pencil, Trash2 } from 'lucide-react';
import Alert from '../common/Alert.js';
import roleService from '../../services/RoleService.js';
import {
  PageLayout,
  Button,
  DataTable,
} from '../common/index.js';

const RoleList = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const response = await roleService.getAll();
      setRoles(response.data || []);
    } catch (err) {
      Alert.error('Failed to load roles');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (roleId) => {
    const confirmed = await Alert.confirm(
      'Are you sure you want to delete this role? This action cannot be undone.',
      'Delete Role'
    );

    if (confirmed) {
      try {
        await roleService.delete(roleId);
        Alert.success('Role deleted successfully!');
        fetchRoles();
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to delete role');
      }
    }
  };

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });

  const columns = [
    {
      key: 'role_name',
      label: 'Role Name',
      render: (role) => (
        <button
          onClick={() => navigate(`/roles/view/${role.id}`)}
          className="font-medium text-brand hover:underline text-left"
        >
          {role.role_name}
        </button>
      ),
    },
    {
      key: 'created_by',
      label: 'Created By',
      render: (role) => role.created_by || 'System',
    },
    {
      key: 'created_at',
      label: 'Created Date',
      render: (role) => formatDate(role.created_at),
    },
    {
      key: 'updated_at',
      label: 'Last Updated',
      render: (role) => formatDate(role.updated_at),
    },
  ];

  const getActions = (role) => [
    {
      label: 'View Details',
      icon: Eye,
      onClick: () => navigate(`/roles/view/${role.id}`),
    },
    {
      label: 'Edit',
      icon: Pencil,
      onClick: () => navigate(`/roles/edit/${role.id}`),
    },
    {
      label: 'Delete',
      icon: Trash2,
      variant: 'danger',
      onClick: () => handleDelete(role.id),
    },
  ];

  return (
    <PageLayout
      title="Roles"
      icon={Settings}
      actions={
        <>
          <Button icon={Plus} onClick={() => navigate('/users/add')}>
            Add User
          </Button>
          <Button icon={Plus} onClick={() => navigate('/roles/add')}>
            Add Roles
          </Button>
        </>
      }
    >
      <DataTable
        columns={columns}
        data={roles}
        loading={loading}
        emptyMessage="No roles found"
        rowKey="id"
        actions={getActions}
      />
    </PageLayout>
  );
};

export default RoleList;
